/**
 * The first run tour.
 *
 * Five steps, each one pointing at an element that is on the screen at that
 * moment, each one dismissible, and the whole thing respected forever once a
 * person says no. It teaches. It does not congratulate, it does not celebrate
 * and it does not pretend to be a person.
 *
 * WHY IT DOES NOT BLOCK. Every layer except the step card has
 * pointer-events: none, so the tour is an annotation over a working product
 * rather than a gate in front of one. A person who ignores the card and clicks
 * New project gets a new project, and the tour gets out of the way. This is
 * the difference between a tour and a modal wizard, and it is the whole reason
 * a tour is acceptable at all on a console.
 *
 * THE FOCUS CONTRACT, which is the part that gets skipped and then costs a
 * keyboard user the page:
 *   - each step moves focus to the card, so the next thing Tab reaches is the
 *     step's own controls rather than whatever the page had focused;
 *   - Tab and Shift Tab cycle inside the card;
 *   - Escape ends the tour, from anywhere, at any step;
 *   - the anchored element is given an accessible description pointing at the
 *     step's text, so a screen reader user who tabs to the anchor after the
 *     tour ends still hears what the tour said about it, and the description
 *     is removed cleanly afterwards;
 *   - ending returns focus to whatever had it when the step opened.
 *
 * MOTION. One thing moves: the ring, when it travels between anchors. The card
 * fades. Under prefers-reduced-motion both resolve to a still, in tour.css.
 *
 * WHY IT LIVES OFF THE TOP BAR. The tour points at the rail, at the top bar
 * and at the page, so it cannot belong to any one of them. The top bar is the
 * one component that is mounted on every screen inside the shell and is owned
 * by this pass, so it is the mount point. Nothing about the tour depends on
 * that choice: it finds its anchors in the document by attribute.
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';

import { Button } from '../primitives/Button';
import { t } from '../../lib/i18n/t';
import {
  TOUR_STEPS,
  endTour,
  nextTourStep,
  previousTourStep,
  startTour,
  useTour,
  useTourDone,
} from '../../lib/hooks/use-tour';
import { useDemoStore } from '../../lib/mock/store';
import '../../styles/components/tour.css';

const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** The gap between the ring and the card, and the card and the window edge. */
const GAP = 12;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function measure(name: string | undefined): Rect | null {
  if (!name) return null;
  const node = document.querySelector<HTMLElement>(`[data-tour="${name}"]`);
  if (!node) return null;
  const box = node.getBoundingClientRect();
  // A hidden anchor is not an anchor. The rail is display:none under 900px and
  // still reports a zero box, which would otherwise draw a ring on nothing.
  if (box.width === 0 || box.height === 0) return null;
  return { top: box.top, left: box.left, width: box.width, height: box.height };
}

function same(a: Rect, b: Rect): boolean {
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function anchorNode(name: string | undefined): HTMLElement | null {
  if (!name) return null;
  return document.querySelector<HTMLElement>(`[data-tour="${name}"]`);
}

function clamp(value: number, low: number, high: number): number {
  if (high < low) return low;
  return Math.min(Math.max(value, low), high);
}

export function SpotlightTour() {
  const { running, index } = useTour();
  const done = useTourDone();
  // The Alpha welcome dialog goes first. Arming under its scrim would spend
  // step one on a page nobody can see yet.
  const welcomeSeen = useDemoStore((s) => s.prefs.alphaWelcomeSeen === true);
  const location = useLocation();

  const cardRef = useRef<HTMLDivElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  // The step's second element, when it has one. It gets a ring of its own and
  // is deliberately kept out of the card placement below.
  const [alsoRect, setAlsoRect] = useState<Rect | null>(null);
  const [card, setCard] = useState({ width: 360, height: 220 });

  const step = TOUR_STEPS[index];
  const bodyId = 'tour-body';
  const titleId = 'tour-title';

  /* -------------------------------------------------------- the auto start */

  // It arms on the projects screen only, and only once the thing the first
  // step points at is actually on the page. Starting against a loading
  // skeleton would spotlight a rectangle that is about to be replaced.
  const armed = useRef(false);

  // The demo dock's "Reset the demo" clears prefs, which sets tourDone back to
  // false. Without this the ref would still say "already armed this session"
  // and the reset would silently fail to bring the tour back, which is the one
  // thing the dock exists to make reliable in front of an audience.
  useEffect(() => {
    if (!done) armed.current = false;
  }, [done]);

  useEffect(() => {
    if (done || running || armed.current || !welcomeSeen) return;
    if (location.pathname !== '/projects') return;

    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (measure(TOUR_STEPS[0].anchor)) {
        window.clearInterval(timer);
        armed.current = true;
        startTour();
      } else if (tries > 30) {
        window.clearInterval(timer);
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, [done, running, welcomeSeen, location.pathname]);

  /* ------------------------------------------------------------ measuring */

  // It writes state only when the box actually moved. The interval below polls
  // three times a second, and a fresh object every tick would re-render the
  // whole overlay three times a second for nothing.
  const remeasure = useCallback(() => {
    if (!running || !step) return;
    const next = measure(step.anchor) ?? measure(step.fallbackAnchor);
    setRect((prev) => {
      if (prev === next) return prev;
      if (prev && next && same(prev, next)) return prev;
      return next;
    });
    const also = measure(step.alsoAnchor);
    setAlsoRect((prev) => {
      if (prev === also) return prev;
      if (prev && also && same(prev, also)) return prev;
      return also;
    });
  }, [running, step]);

  useLayoutEffect(() => {
    remeasure();
  }, [remeasure]);

  useEffect(() => {
    if (!running) return;
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    // Cheap insurance against a layout the tour cannot subscribe to: usage
    // numbers landing under the ledger move every anchor below them.
    const timer = window.setInterval(remeasure, 300);
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
      window.clearInterval(timer);
    };
  }, [running, remeasure]);

  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    const box = node.getBoundingClientRect();
    setCard((prev) =>
      prev.width === box.width && prev.height === box.height
        ? prev
        : { width: box.width, height: box.height },
    );
  }, [running, index, rect]);

  /* --------------------------------------------------------------- focus */

  useEffect(() => {
    if (!running) return;
    returnTo.current = document.activeElement as HTMLElement | null;
    return () => {
      returnTo.current?.focus();
    };
    // Deliberately not keyed on index: focus returns to where the person was
    // when the tour opened, not to where they were one step ago.
  }, [running]);

  useEffect(() => {
    if (!running) return;
    cardRef.current?.focus();
  }, [running, index]);

  // The anchor gets an accessible description that is the step's own text.
  useEffect(() => {
    if (!running || !step) return;
    const node = anchorNode(step.anchor) ?? anchorNode(step.fallbackAnchor);
    if (!node) return;
    const had = node.getAttribute('aria-describedby');
    node.setAttribute('aria-describedby', had ? `${had} ${bodyId}` : bodyId);
    return () => {
      if (had) node.setAttribute('aria-describedby', had);
      else node.removeAttribute('aria-describedby');
    };
  }, [running, step]);

  /* ------------------------------------------------------ leaving the page */

  const pathname = location.pathname;
  const startedAt = useRef(pathname);
  useEffect(() => {
    if (!running) {
      startedAt.current = pathname;
      return;
    }
    if (pathname !== startedAt.current) endTour();
  }, [pathname, running]);

  /* ------------------------------------------------------------- keyboard */

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      endTour();
      return;
    }
    if (event.key !== 'Tab') return;

    const panel = cardRef.current;
    if (!panel) return;
    const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (nodes.length === 0) {
      event.preventDefault();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  // Escape works even when focus has wandered out of the card, which it can:
  // the overlay lets clicks through, so a person can click the page and then
  // press Escape expecting the tour to go.
  useEffect(() => {
    if (!running) return;
    function onGlobalKey(event: KeyboardEvent) {
      if (event.key === 'Escape') endTour();
    }
    window.addEventListener('keydown', onGlobalKey);
    return () => window.removeEventListener('keydown', onGlobalKey);
  }, [running]);

  if (!running || !step) return null;

  /* ------------------------------------------------------------ placement */

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = Math.round(vh / 2 - card.height / 2);
  let left = Math.round(vw / 2 - card.width / 2);

  // Below, then beside, then above, then the bottom of the window. Beside
  // beats above because a card placed above an anchor near the foot of the
  // page covers the thing the previous step just explained, and the empty
  // account's action sits under three paragraphs it would land on top of.
  if (rect) {
    if (rect.top + rect.height + GAP + card.height <= vh - GAP) {
      top = rect.top + rect.height + GAP;
      left = rect.left;
    } else if (rect.left + rect.width + GAP + card.width <= vw - GAP) {
      left = rect.left + rect.width + GAP;
      top = rect.top;
    } else if (rect.top - GAP - card.height >= GAP) {
      top = rect.top - GAP - card.height;
      left = rect.left;
    } else {
      left = rect.left;
      top = vh - card.height - GAP;
    }
    left = clamp(left, GAP, vw - card.width - GAP);
    top = clamp(top, GAP, vh - card.height - GAP);
  }

  const last = index === TOUR_STEPS.length - 1;

  const overlay = (
    <div className="tour" data-anchored={rect ? 'yes' : 'no'} aria-hidden={false}>
      {[rect, alsoRect].map((one, i) =>
        one ? (
          <Fragment key={i}>
            <span
              className="tour__hole"
              style={{
                top: one.top - 4,
                left: one.left - 4,
                width: one.width + 8,
                height: one.height + 8,
              }}
            />
            <span
              className="tour__ring"
              style={{
                top: one.top - 4,
                left: one.left - 4,
                width: one.width + 8,
                height: one.height + 8,
              }}
            />
          </Fragment>
        ) : null,
      )}

      <div
        className="tour__card"
        role="dialog"
        aria-modal={false}
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        ref={cardRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{ '--tour-top': `${top}px`, '--tour-left': `${left}px` } as React.CSSProperties}
      >
        <p className="tour__step">
          {t('Step {n} of {total}', { n: index + 1, total: TOUR_STEPS.length })}
        </p>

        <h2 className="tour__title" id={titleId}>
          {t(step.title)}
        </h2>

        <p className="tour__body" id={bodyId}>
          {t(step.body)}
        </p>

        <div className="tour__track" role="presentation">
          {TOUR_STEPS.map((one, i) => (
            <span className="tour__tick" data-on={i <= index ? 'yes' : 'no'} key={one.anchor} />
          ))}
        </div>

        <div className="tour__foot">
          <button type="button" className="tour__skip" onClick={() => endTour(true)}>
            {t('Skip the tour')}
          </button>

          {index > 0 ? (
            <Button variant="ghost" size="sm" onClick={previousTourStep}>
              {t('Back')}
            </Button>
          ) : null}

          {/* Outline, never solid. The screen's one ink block is New project,
              and on the first step it is the thing inside the ring. A filled
              Next here would put a second ink block on the screen and would
              argue that reading the tour is more important than using the
              product, which it is not. */}
          <Button variant="outline" size="sm" onClick={last ? () => endTour() : nextTourStep}>
            {last ? t('Done') : t('Next')}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
